import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField,
  Typography,
  styled,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import React, { useState } from 'react'
import ScoreBadge from '@components/ScoreBadge/Scorebadge.component'
import { 
  AccessTimeOutlined, 
  CalendarTodayOutlined, 
  ChevronRightOutlined,
} from '@mui/icons-material'
import AndroidIcon from 'icons/Technologies/Android.icon'
import SearchIcon from 'icons/Search.icon'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import SmallBadgeIcon from "assets/images/history-table/small-badge.png";
import SmallDocs from "assets/images/history-table/small-docs.png";
import SmallWatch from "assets/images/history-table/small-watch.png";
import Frame from "assets/images/history-table/frame.png";

export const Route = createFileRoute('/(dashboard)/_dashboard/history')({
  component: RouteComponent,
})

const StyledSearch = styled(TextField)(() => ({
  input: {
    lineHeight: '20px',
  },

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--Interviewer-Lightest-Black)',
  },

  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: '1px solid var(--Interviewer-Black)',
  },
  '&:focused .MuiOutlinedInput-notchedOutline': {
    border: '1px solid var(--Interviewer-Black)',
  },
}));

interface Interview {
  id: string;
  totalTime: number;
  difficulty: string;
  status: number;
  createdOnUtc: string;
  correctQuestions: number;
  totalQuestions: number;
}


function RouteComponent() {
    
    const navigate = useNavigate()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(3)
    const [searchTerm, setSearchTerm] = useState('')


    const { data: interviews, isLoading } = useQuery({
        queryKey: ['interviews'],
        queryFn: async () => {
        const token = localStorage.getItem('token');
        const response = await axios.get('https://interviewer.runasp.net/api/submittedInterviews?sortBy=CreatedOnUtc&sortOrder=DESC', {
            headers: {
            Authorization: `Bearer ${token}`,
            },
        });
        return response.data as Interview[];
        },
    });

    const handleChangePage = (_: unknown, newPage: number) => setPage(newPage)

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleRowClick = (interview: Interview) => {
        // Don't navigate if status is 2
        if (interview.status === 1) {
            return;
        }
        
        navigate({ 
            to: '/history-details/$id',
            params: {id: interview.id},
            state: {
               interview
            } as any
        })
    }

    // Group interviews by date
    const groupedInterviews = interviews?.reduce((groups: { [key: string]: Interview[] }, interview) => {
        const date = new Date(interview.createdOnUtc).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
        });
        if (!groups[date]) {
        groups[date] = [];
        }
        groups[date].push(interview);
        return groups;
    }, {}) || {};

    // Convert to array and filter based on search
    const filteredGroups = Object.entries(groupedInterviews)
        .map(([date, interviews]) => ({
        date,
        technologies: interviews.filter(interview =>
            (interview.difficulty || '').toLowerCase().includes(searchTerm.toLowerCase())
        )
        }))
        .filter(group => group.technologies.length > 0);

    // Pagination at group (date) level
    const paginatedGroups = filteredGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

    if (isLoading) {
        return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
        );
    }


    // Odredjivanje bedza (boja)
    const getScoreBadgeType = (total:number, correct:number) => {
        if (total === 0) return 'error'

        const percentage = (correct / total) * 100;

        if (percentage >= 70) return 'success';
        if (percentage >= 40) return 'warning';

        return 'error';
    }

  return (
    <Box sx={{
        // width: {xs: '100%', md: '80%'},
        // mx: 'auto',
        padding: {xs: '20px 16px', md: '75px 75px'}
    }}>
        <Box sx={{ borderBottom: '1px solid var(--Interviewer-Near-White)', pb: '32px'}}>
            <Typography variant="h1">Interview History</Typography>
        </Box>
        <Box>
            <StyledSearch
                fullWidth
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{py: { xs: '16px', md: '24px' }, borderBottom: '1px solid var(--Interviewer-Near-White)'}}
                slotProps={{
                input: {
                    startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon />
                    </InputAdornment>
                    ),
                },
                }}
                placeholder="Search by difficulty"
            />
        </Box>

        <Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ 
                                borderBottom: 'none',
                                display: { xs: 'none', md: 'table-cell' }
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarTodayOutlined fontSize='small' />
                                    <Typography variant='h5'>Date</Typography>
                                    {/* <UnfoldMore sx={{ fontSize: 18 }} /> */}
                                </Box>
                            </TableCell>
                            <TableCell colSpan={5} sx={{ borderBottom: 'none', p: 1 }}>
                                <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 48px',
                                gap: { xs: 3, md: 2 },
                                alignItems: 'center',
                                p: { xs: 2, md: 2 }
                                }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    minWidth: '120px'
                                }}>
                                    <img src={Frame} alt="Icon" />
                                    <Typography variant='h5'>Technology</Typography>
                                    {/* <UnfoldMore sx={{ fontSize: 18}} /> */}
                                </Box>
                                <Box sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    minWidth: '100px'
                                }}>
                                    <img src={SmallDocs} alt="Icon" />
                                    <Typography variant='h5'>Field</Typography>
                                    {/* <UnfoldMore sx={{ fontSize: 18}} /> */}
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    minWidth: '100px'
                                }}>
                                    <img src={SmallWatch} alt="Icon" />
                                    <Typography variant='h5'>Duration</Typography>
                                    {/* <UnfoldMore sx={{ fontSize: 18 }} /> */}
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    minWidth: '100px'
                                }}>
                                    <AccessTimeOutlined fontSize='small'/>
                                    <Typography variant='h5'>Time</Typography>
                                    {/* <UnfoldMore sx={{ fontSize: 18 }} /> */}
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    minWidth: '100px'
                                }}>
                                    <img src={SmallBadgeIcon} alt="Icon" />
                                    <Typography variant='h5'>Score</Typography>
                                    {/* <UnfoldMore sx={{ fontSize: 18 }} /> */}
                                </Box>
                                <Box sx={{ minWidth: '48px' }}></Box>
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedGroups.map((group, groupIndex) =>
                        group.technologies.map((interview, index) => (
                            <React.Fragment key={`${group.date}-${index}`}>
                            {index === 0 && (
                                <TableRow>
                                <TableCell 
                                    colSpan={6} 
                                    sx={{
                                    borderBottom: 'none',
                                    pt: groupIndex === 0 ? 2 : 4,
                                    pb: 2,
                                    display: { xs: 'table-cell', md: 'none' }
                                    }}
                                >
                                    <Typography 
                                    variant="h3" 
                                    sx={{ 
                                        fontWeight: 700,
                                        color: '#101828',
                                        fontSize: '16px'
                                    }}
                                    >
                                    {group.date}
                                    </Typography>
                                </TableCell>
                                </TableRow>
                            )}
                            <TableRow 
                                onClick={() => handleRowClick(interview)}
                                sx={{
                                ...(index === 0 && groupIndex !== 0 ? { 
                                    '& > td': { paddingTop: '32px' }
                                } : {}),
                                ...(interview.status === 1 ? {
                                    '& > td': { opacity: 0.5 },
                                    cursor: 'not-allowed'
                                } : {})
                                }}
                            >
                                {index === 0 && (
                                <TableCell
                                    rowSpan={group.technologies.length}
                                    sx={{
                                    fontWeight: 700,
                                    color: '#101828',
                                    verticalAlign: 'top',
                                    borderBottom: 'none',
                                    pt: 3,
                                    fontSize: '16px',
                                    display: { xs: 'none', md: 'table-cell' }
                                    }}
                                >
                                    {group.date}
                                </TableCell>
                                )}
                                <TableCell 
                                colSpan={5} 
                                sx={{ 
                                    p: 1, 
                                    borderBottom: 'none',
                                }}
                                >
                                <Box sx={{
                                    backgroundColor: 'white',
                                    border: '1px solid var(--Light-Gray)',
                                    borderRadius: '12px',
                                    p: { xs: 2, md: 2 },
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 48px',
                                    alignItems: 'center',
                                    gap: { xs: 3, md: 2 },
                                    boxShadow: '0px 1px 2px 0px #0205111A',
                                    ...(interview.status === 1 ? {
                                        opacity: 0.5,
                                        cursor: 'not-allowed',
                                        '&:hover': {
                                            backgroundColor: 'white'
                                        }
                                    } : {
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        },
                                        cursor: 'pointer'
                                    }),
                                }}>
                                    <Box sx={{
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '12px',
                                        minWidth: '120px'
                                    }}>
                                        <AndroidIcon />
                                        <Typography variant='h3' sx={{fontWeight: '600'}}>{interview.difficulty}</Typography>
                                    </Box>
                                    <Box sx={{ minWidth: '100px' }}>
                                        <Typography variant='h3' sx={{fontWeight: '600'}}>Interview</Typography>
                                    </Box>
                                    <Box sx={{ minWidth: '100px' }}>
                                        <Typography variant='h3' sx={{fontWeight: '600'}}>
                                            {Math.round(interview.totalTime / 60)}m
                                        </Typography>
                                    </Box>
                                    <Box sx={{ minWidth: '100px' }}>
                                        <Typography variant='h3' sx={{fontWeight: '600'}}>
                                            {new Date(interview.createdOnUtc).toLocaleTimeString()}
                                        </Typography>  
                                    </Box>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'flex-start',
                                        minWidth: '100px'
                                    }}>
                                        <ScoreBadge 
                                            value={`${interview.correctQuestions}/${interview.totalQuestions}`} 
                                            type={getScoreBadgeType(
                                                interview.totalQuestions,
                                                interview.correctQuestions
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'center',
                                        minWidth: '48px'
                                    }}>
                                        <ChevronRightOutlined sx={{ color: '#667085' }} />
                                    </Box>
                                </Box>
                                </TableCell>
                            </TableRow>
                            </React.Fragment>
                        ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={filteredGroups.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                    display: 'flex',
                    mr: 'auto'
                }}
            />
        </Box>
    </Box>
  )
}
